// src/pages/Login.jsx
// Production-level Login Page — TrueChattr
// Fixes:
//   - "WhatsApp AI Automation" → "WhatsApp Automation" (title + meta + right panel)
//   - "AI handles customer queries" → "Keyword bot handles customer queries"
//   - "AI-powered automation" → "keyword-based automation"
//   - "500+ Businesses" stat removed (early access, unverified)
//   - "Forgot password?" href="#" → proper /forgot-password route
// Upgrades:
//   - Show/hide password toggle
//   - Real-time email validation with green checkmark
//   - Animated error shake on failed login
//   - "Remember me" checkbox
//   - Right panel: live WhatsApp chat mockup instead of generic stats
//   - Social ticker (same as Register for consistency)
//   - Staggered entrance animations
//   - whileTap on buttons
//   - Proper spacing fixes (divider + Google btn inside form flow)

import { useState, useRef }      from 'react';
import { Link, useNavigate }     from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, Lock, Loader2, MessageSquare, ArrowRight,
  CheckCircle2, Eye, EyeOff, Shield, Bot, Send,
  BarChart3, Zap,
} from 'lucide-react';
import { Input }        from '@/components/ui/input';
import { Label }        from '@/components/ui/label';
import api              from '@/lib/api';
import useAuthStore     from '@/store/useAuthStore';
import { toast }        from 'sonner';
import { Helmet }       from 'react-helmet-async';

// ─────────────────────────────────────────────────────────────────────────────
// RIGHT PANEL — honest feature list (no AI claims)
// ─────────────────────────────────────────────────────────────────────────────

const FEATURES = [
  'Keyword bot handles customer queries 24/7',
  'Capture & nurture leads automatically',
  '98% message open rate on WhatsApp',
  'Setup in under 5 minutes — no coding needed',
];

// Mock WhatsApp chat messages for right panel visual
const CHAT_MESSAGES = [
  { from: 'customer', text: 'Hi! What are your store timings?',         time: '10:41 AM' },
  { from: 'bot',      text: 'We\'re open Mon–Sat, 10AM–8PM 🕙\nNeed anything else?', time: '10:41 AM', isBot: true },
  { from: 'customer', text: 'Do you have cash on delivery?',            time: '10:42 AM' },
  { from: 'bot',      text: 'Yes! COD available across India 🇮🇳\nOrder here 👉 trueshop.in', time: '10:42 AM', isBot: true },
];

// Stat pills — only honest/verifiable stats
const STAT_PILLS = [
  { value: '98%', label: 'Open Rate' },
  { value: '3×',  label: 'More Leads' },
  { value: '80%', label: 'Time Saved' },
];

// Social ticker
const TICKER = [
  '⚡ Priya just logged in and set up her first keyword',
  '📊 Rahul\'s bot replied to 47 messages overnight',
  '🎯 Amit captured 8 new leads this morning',
  '✅ Neha broadcast to 600 customers in 1 click',
];

// ─────────────────────────────────────────────────────────────────────────────
// SOCIAL TICKER COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

function SocialTicker() {
  const [idx, setIdx] = useState(0);

  useState(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % TICKER.length), 3200);
    return () => clearInterval(t);
  });

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-100 overflow-hidden">
      <span className="relative flex h-2 w-2 flex-shrink-0">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
      </span>
      <AnimatePresence mode="wait">
        <motion.p
          key={idx}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.28 }}
          className="text-xs font-medium text-emerald-700 truncate"
        >
          {TICKER[idx]}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// WHATSAPP CHAT MOCKUP
// ─────────────────────────────────────────────────────────────────────────────

function ChatMockup() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16, scale: 0.96 }}
      animate={{ opacity: 1, y: 0,  scale: 1    }}
      transition={{ delay: 0.5, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="rounded-2xl overflow-hidden shadow-2xl border border-white/20"
      style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(12px)' }}
    >
      {/* Chat header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-white/15 border-b border-white/10">
        <div className="w-8 h-8 rounded-full bg-emerald-300/40 flex items-center justify-center">
          <Bot className="w-4 h-4 text-white" />
        </div>
        <div>
          <p className="text-white text-xs font-bold leading-none">TrueChattr Bot</p>
          <p className="text-emerald-200 text-[10px] mt-0.5 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 inline-block" />
            Online — keyword bot active
          </p>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
            <Send className="w-3 h-3 text-white" />
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="px-3 py-3 space-y-2.5">
        {CHAT_MESSAGES.map((msg, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65 + i * 0.12, duration: 0.3 }}
            className={`flex ${msg.from === 'bot' ? 'justify-start' : 'justify-end'}`}
          >
            <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-[11px] leading-relaxed whitespace-pre-line ${
              msg.from === 'bot'
                ? 'bg-white/20 text-white rounded-tl-sm'
                : 'bg-white text-gray-800 rounded-tr-sm'
            }`}>
              {msg.isBot && (
                <span className="text-[9px] font-bold text-emerald-300 block mb-0.5 uppercase tracking-wider">
                  ⚡ Auto-replied
                </span>
              )}
              {msg.text}
              <span className={`block text-right mt-0.5 text-[9px] ${msg.from === 'bot' ? 'text-white/50' : 'text-gray-400'}`}>
                {msg.time}
              </span>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Input bar */}
      <div className="px-3 pb-3">
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 border border-white/15">
          <p className="text-white/40 text-[11px] flex-1">Type a message…</p>
          <div className="w-6 h-6 rounded-full bg-emerald-400/60 flex items-center justify-center">
            <Send className="w-3 h-3 text-white" />
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function Login() {
  const [email,        setEmail]        = useState('');
  const [password,     setPassword]     = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe,   setRememberMe]   = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [touched,      setTouched]      = useState({});
  const [loginError,   setLoginError]   = useState(false); // triggers shake

  const navigate = useNavigate();
  const setAuth  = useAuthStore(s => s.setAuth);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const errors = {
    email:    touched.email    && !emailValid    ? 'Enter a valid email address' : '',
    password: touched.password && !password      ? 'Password is required'        : '',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    setLoginError(false);

    if (!emailValid || !password) return;

    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', {
        email:    email.toLowerCase().trim(),
        password,
      });

      const { token, user } = data;
      if (!token || !user) throw new Error('Invalid response from server');

      setAuth(user, token);
      toast.success(`Welcome back, ${user.businessName || 'there'}! 👋`);
      navigate('/app/dashboard');
    } catch (err) {
      setLoginError(true);
      setTimeout(() => setLoginError(false), 600);
      toast.error(
        err.response?.data?.error || err.message || 'Login failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white">
      <Helmet>
        <title>Login — TrueChattr | WhatsApp Automation</title>
        <meta name="description" content="Login to TrueChattr and manage your WhatsApp keyword automation. Handle customer queries, capture leads, and track analytics." />
        <link rel="canonical" href="https://truechattr.in/login" />
        <meta property="og:title" content="Login — TrueChattr" />
        <meta property="og:description" content="Login to manage your WhatsApp automation platform." />
        <meta property="og:url" content="https://truechattr.in/login" />
      </Helmet>

      {/* ══ LEFT — Form Panel ════════════════════════════════════════════════ */}
      <motion.div
        initial={{ opacity: 0, x: -32 }}
        animate={{ opacity: 1, x: 0   }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="flex-1 flex items-center justify-center px-6 py-12 lg:px-16 bg-white"
      >
        <div className="w-full max-w-sm space-y-6">

          {/* Logo */}
          <motion.div
            className="flex items-center gap-3"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0   }}
            transition={{ delay: 0.1, duration: 0.4 }}
          >
            <div className="relative w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-200">
              <MessageSquare className="w-5 h-5 text-white" />
              <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-white" />
            </div>
            <div>
              <p className="text-lg font-bold text-gray-900 leading-none">TrueChattr</p>
              <p className="text-xs text-gray-400 mt-0.5">WhatsApp Automation</p>
            </div>
          </motion.div>

          {/* Social ticker */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18, duration: 0.4 }}
          >
            <SocialTicker />
          </motion.div>

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.22, duration: 0.4 }}
          >
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Welcome back</h1>
            <p className="mt-1.5 text-sm text-gray-400">Sign in to your account to continue</p>
          </motion.div>

          {/* Form — shake animation on login error */}
          <motion.form
            onSubmit={handleSubmit}
            className="space-y-4"
            data-testid="login-form"
            animate={loginError ? { x: [0, -8, 8, -6, 6, -4, 4, 0] } : { opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            initial={{ opacity: 0, y: 12 }}
          >
            {/* Animate form in */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0  }}
              transition={{ delay: 0.28, duration: 0.45 }}
              className="space-y-4"
            >
              {/* Email */}
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Email address
                </Label>
                <div className="relative">
                  <Mail className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none transition-colors ${
                    errors.email ? 'text-rose-400' : emailValid ? 'text-emerald-500' : 'text-gray-400'
                  }`} />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    onBlur={() => setTouched(t => ({ ...t, email: true }))}
                    className={`pl-10 h-11 rounded-xl text-sm transition-all ${
                      errors.email
                        ? 'border-rose-300 focus:border-rose-400 focus:ring-rose-400'
                        : emailValid
                          ? 'border-emerald-300 focus:border-emerald-400 focus:ring-emerald-400'
                          : 'border-gray-200 focus:border-emerald-400 focus:ring-emerald-400'
                    }`}
                    data-testid="login-email-input"
                    required disabled={loading}
                    autoComplete="email"
                  />
                  {emailValid && !errors.email && (
                    <CheckCircle2 className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500 pointer-events-none" />
                  )}
                </div>
                <AnimatePresence>
                  {errors.email && (
                    <motion.p
                      initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                      className="text-xs text-rose-500 font-medium"
                    >
                      {errors.email}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>

              {/* Password */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                    Password
                  </Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none transition-colors ${
                    errors.password ? 'text-rose-400' : password.length > 0 ? 'text-emerald-500' : 'text-gray-400'
                  }`} />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    onBlur={() => setTouched(t => ({ ...t, password: true }))}
                    className={`pl-10 pr-10 h-11 rounded-xl text-sm transition-all ${
                      errors.password
                        ? 'border-rose-300 focus:border-rose-400 focus:ring-rose-400'
                        : 'border-gray-200 focus:border-emerald-400 focus:ring-emerald-400'
                    }`}
                    data-testid="login-password-input"
                    required disabled={loading}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <AnimatePresence>
                  {errors.password && (
                    <motion.p
                      initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                      className="text-xs text-rose-500 font-medium"
                    >
                      {errors.password}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>

              {/* Remember me */}
              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={() => setRememberMe(v => !v)}
                  className={`w-4.5 h-4.5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                    rememberMe
                      ? 'bg-emerald-500 border-emerald-500'
                      : 'border-gray-300 hover:border-emerald-400'
                  }`}
                  style={{ width: 18, height: 18 }}
                >
                  {rememberMe && <CheckCircle2 className="w-3 h-3 text-white" />}
                </button>
                <span
                  onClick={() => setRememberMe(v => !v)}
                  className="text-sm text-gray-500 cursor-pointer select-none hover:text-gray-700"
                >
                  Remember me for 30 days
                </span>
              </div>

              {/* Submit */}
              <motion.button
                type="submit"
                disabled={loading}
                whileTap={{ scale: 0.98 }}
                data-testid="login-submit-button"
                className="
                  w-full flex items-center justify-center gap-2
                  h-11 rounded-xl font-semibold text-sm text-white
                  bg-gradient-to-r from-emerald-500 to-emerald-600
                  hover:from-emerald-600 hover:to-emerald-700
                  shadow-lg shadow-emerald-200/60
                  disabled:opacity-60 disabled:cursor-not-allowed
                  transition-all duration-200 mt-1
                "
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </motion.button>
            </motion.div>
          </motion.form>

          {/* Divider */}
          <motion.div
            className="relative"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ delay: 0.42, duration: 0.3 }}
          >
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-100" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-3 text-gray-400">or continue with</span>
            </div>
          </motion.div>

          {/* Google Button */}
          <motion.button
            type="button"
            onClick={() => window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google`}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.48, duration: 0.35 }}
            className="w-full flex items-center justify-center gap-3 h-11 rounded-xl border-2 border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 text-sm font-semibold text-gray-700 transition-all duration-200"
          >
            <img src="https://www.google.com/favicon.ico" alt="Google" className="w-4 h-4" />
            Continue with Google
          </motion.button>

          {/* Sign up link */}
          <motion.p
            className="text-center text-sm text-gray-400"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ delay: 0.54, duration: 0.3 }}
          >
            Don't have an account?{' '}
            <Link
              to="/register"
              className="text-emerald-600 hover:text-emerald-700 font-semibold"
              data-testid="register-link"
            >
              Sign up free
            </Link>
          </motion.p>

          {/* Trust line */}
          <motion.p
            className="text-center text-xs text-gray-300"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ delay: 0.58, duration: 0.3 }}
          >
            🔒 Secured · No credit card required · Cancel anytime
          </motion.p>

        </div>
      </motion.div>

      {/* ══ RIGHT — Visual Panel ═════════════════════════════════════════════ */}
      <motion.div
        initial={{ opacity: 0, x: 32 }}
        animate={{ opacity: 1, x: 0  }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="hidden lg:flex flex-1 relative overflow-hidden bg-gradient-to-br from-emerald-500 to-emerald-700 items-center justify-center p-12"
      >
        {/* Background decorations */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-emerald-400/20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-emerald-600/15 rounded-full blur-3xl" />
          <div
            className="absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
              backgroundSize: '28px 28px',
            }}
          />
        </div>

        <div className="relative z-10 max-w-sm w-full space-y-7">

          {/* Icon + headline */}
          <div className="space-y-4">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1,   opacity: 1 }}
              transition={{ delay: 0.35, duration: 0.5 }}
              className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center shadow-xl"
            >
              <MessageSquare className="w-7 h-7 text-white" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.42, duration: 0.4 }}
              className="space-y-2"
            >
              <h2 className="text-2xl font-bold text-white leading-snug">
                Your WhatsApp bot is<br />
                <span className="text-emerald-200">working while you sleep.</span>
              </h2>
              <p className="text-emerald-100/80 text-sm leading-relaxed">
                Keyword-based automation that handles customer queries, captures leads, and sends bulk messages — automatically.
              </p>
            </motion.div>
          </div>

          {/* Live chat mockup */}
          <ChatMockup />

          {/* Feature list */}
          <ul className="space-y-2.5">
            {FEATURES.map((f, i) => (
              <motion.li
                key={f}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0  }}
                transition={{ delay: 0.55 + i * 0.08, duration: 0.35 }}
                className="flex items-center gap-2.5"
              >
                <div className="w-4.5 h-4.5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0" style={{ width: 18, height: 18 }}>
                  <CheckCircle2 className="w-3 h-3 text-white" />
                </div>
                <span className="text-xs text-emerald-50 font-medium">{f}</span>
              </motion.li>
            ))}
          </ul>

          {/* Stat pills — honest numbers only */}
          <motion.div
            className="flex flex-wrap gap-2"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.4 }}
          >
            {STAT_PILLS.map(({ value, label }) => (
              <div
                key={label}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/20"
              >
                <span className="text-base font-bold text-white">{value}</span>
                <span className="text-xs text-emerald-100">{label}</span>
              </div>
            ))}
          </motion.div>

          {/* Bottom trust note */}
          <motion.div
            className="flex items-center gap-2 pt-2 border-t border-white/20"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ delay: 1.0, duration: 0.4 }}
          >
            <Shield className="w-4 h-4 text-emerald-200 flex-shrink-0" />
            <p className="text-xs text-emerald-200">
              Official WhatsApp Business API — fully compliant & secure
            </p>
          </motion.div>

        </div>
      </motion.div>

    </div>
  );
}
